import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  LocalCafe as LocalCafeIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { DriverShiftSummary } from "../hooks/useDriverSummary";

type FilterType = "all" | "on-shift" | "on-break";

interface FilterBarProps {
  filter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  roleFilter: string;
  onRoleFilterChange: (role: string) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  tasks: Array<{ id: number; key: string; title: string }>;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  summary: Pick<DriverShiftSummary, "onShiftCount" | "onBreakCount" | "totalDriverCount">;
}

function FilterBar({
  filter,
  onFilterChange,
  roleFilter,
  onRoleFilterChange,
  searchQuery,
  onSearchQueryChange,
  tasks,
  searchInputRef,
  summary,
}: FilterBarProps) {
  const { t } = useTranslation();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: FilterType) => {
    onFilterChange(newValue);
  };

  const renderTabLabel = (label: string, count?: number) => {
    if (count === undefined) {
      return label;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{label}</span>
        <Box
          sx={{
            backgroundColor: "action.selected",
            color: "text.primary",
            fontSize: "0.75rem",
            minWidth: 20,
            height: 20,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 6px",
            fontWeight: 500,
          }}
        >
          {count}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        marginBottom: 3,
        flexWrap: "wrap",
        alignItems: "center",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", gap: 2, flexGrow: 1, alignItems: "center" }}>
        <Tabs
          value={filter}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={t("driverShiftTracker.filters.statusFilter")}
          sx={{
            minHeight: 48,
            "& .MuiTabs-flexContainer": {
              flexWrap: { xs: "wrap", md: "nowrap" },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              display: { xs: "none", md: "block" },
            },
          }}
        >
          <Tab
            value="on-shift"
            label={renderTabLabel(t("driverShiftTracker.filters.onShift"), summary.onShiftCount)}
            icon={<AccessTimeIcon />}
            iconPosition="start"
            data-testid="filter-tab-on-shift"
            sx={{
              textTransform: "none",
              minHeight: 48,
              fontSize: "0.875rem",
            }}
          />
          <Tab
            value="on-break"
            label={renderTabLabel(t("driverShiftTracker.filters.onBreak"), summary.onBreakCount)}
            icon={<LocalCafeIcon />}
            iconPosition="start"
            data-testid="filter-tab-on-break"
            sx={{
              textTransform: "none",
              minHeight: 48,
              fontSize: "0.875rem",
            }}
          />
          <Tab
            value="all"
            label={renderTabLabel(t("driverShiftTracker.filters.allDrivers"), summary.totalDriverCount)}
            icon={<PeopleIcon />}
            iconPosition="start"
            data-testid="filter-tab-all-drivers"
            sx={{
              textTransform: "none",
              minHeight: 48,
              fontSize: "0.875rem",
            }}
          />
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", pb: 1, flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
        <Select
          value={roleFilter}
          onChange={(e) => onRoleFilterChange(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 150 },
            backgroundColor: "background.paper",
          }}
          data-testid="role-filter-dropdown"
        >
          <MenuItem value="all">{t("driverShiftTracker.filters.allTasks")}</MenuItem>
          {tasks.map((task) => (
            <MenuItem key={task.id} value={task.key}>
              {task.title}
            </MenuItem>
          ))}
        </Select>

        <TextField
          placeholder={t("driverShiftTracker.filters.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          variant="outlined"
          size="small"
          inputRef={searchInputRef}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            minWidth: { xs: "100%", sm: 250 },
            backgroundColor: "background.paper",
          }}
          data-testid="driver-search-field"
          autoComplete="off"
          aria-label={t("driverShiftTracker.filters.searchAriaLabel")}
        />
      </Box>
    </Box>
  );
};

export default FilterBar;
